import React, { useState } from 'react';
import { api } from '../lib/api';
import { EQUIPMENT_RULES } from '@gng-ehs/shared/equipment-rules';

interface InlineEquipmentTypeFormProps {
  onCreated: (newType: { key: string; label: string; short_code?: string }) => void;
  onCancel: () => void;
}

export function InlineEquipmentTypeForm({ onCreated, onCancel }: InlineEquipmentTypeFormProps) {
  const [typeName, setTypeName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e?: React.MouseEvent | React.FormEvent) => {
    if (e) e.preventDefault();
    if (!typeName.trim()) {
      setError('Type Name is required.');
      return;
    }
    if (!shortCode.trim()) {
      setError('Short Form / Code is required.');
      return;
    }

    const cleanCode = shortCode.trim().toUpperCase();
    const cleanName = typeName.trim();

    // Check if matches built-in type
    const builtinMatch = EQUIPMENT_RULES.find(
      (r) => r.key.toUpperCase() === cleanCode || r.label.toUpperCase() === cleanName.toUpperCase()
    );
    if (builtinMatch) {
      onCreated({ key: builtinMatch.key, label: builtinMatch.label, short_code: builtinMatch.key });
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await api.equipmentTypes.create({
        name: cleanName,
        short_code: cleanCode,
      });
      if (res.data) {
        onCreated({ key: res.data.key, label: res.data.label, short_code: res.data.short_code });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add equipment type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 p-2.5 bg-brand-50 border border-brand-200 rounded-lg space-y-2">
      <div className="flex items-center justify-between text-xs font-semibold text-brand-900">
        <span>+ Add New Custom Equipment Type</span>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700">✕</button>
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            className="input text-xs bg-white"
            placeholder="Type Name (e.g. Bridge Crane)"
            value={typeName}
            onChange={(e) => setTypeName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(e); }}
            autoFocus
          />
          <input
            type="text"
            className="input text-xs font-mono uppercase bg-white"
            placeholder="Short Code (e.g. BCR)"
            value={shortCode}
            onChange={(e) => setShortCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(e); }}
          />
        </div>
        {error && <p className="text-[11px] text-red-600 font-semibold">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-2.5 py-1 text-xs text-gray-600 bg-white hover:bg-gray-100 border border-gray-200 rounded-md cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !typeName.trim() || !shortCode.trim()}
            className="px-3 py-1 text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 rounded-md shrink-0 disabled:opacity-50 cursor-pointer"
          >
            {saving ? 'Saving Type…' : '+ Add Equipment Type'}
          </button>
        </div>
      </div>
    </div>
  );
}
